import React, { useState } from 'react';
import api from '../api/axios';

export interface MessageUser {
  id: number;
  full_name: string;
}

export interface MessageAttachment {
  id: number;
  original_filename: string;
  mime_type?: string;
  size_bytes?: number;
}

export interface MessageEdit {
  id: number;
  old_text?: string;
  edited_at: string;
  edited_by?: MessageUser | null;
}

export interface InquiryMessage {
  id: number;
  direction: 'inbound' | 'outbound';
  text?: string;
  telegram_message_id?: number;
  sender_user_id?: number;
  sender?: MessageUser;
  is_internal: boolean;
  created_at: string;
  attachments: MessageAttachment[];
  is_deleted: boolean;
  deleted_at?: string | null;
  deleted_by?: MessageUser | null;
  edited_at?: string | null;
  edits: MessageEdit[];
}

interface Props {
  threadId: number;
  message: InquiryMessage;
  // Tahrirlash/o'chirish menyusi - faqat bizning tomondagi (outbound/ichki
  // eslatma) xabarda va faqat muallif yoki admin/direktorga. Backend ham
  // mustaqil tekshiradi (routers/inquiries.py _get_own_side_message).
  canManage: boolean;
  // Botda deleteBusinessMessages huquqi bormi - yo'q bo'lsa "o'chirish" mijoz
  // chatida xabarni "(bekor qilindi)" belgisiga almashtiradi (retract).
  telegramCanDelete: boolean;
  onUpdated: (message: InquiryMessage) => void;
  onDownload: (att: MessageAttachment) => void;
}

const formatSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const timeShort = (s?: string | null) => {
  if (!s) return '';
  return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const errDetail = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const InquiryMessageBubble: React.FC<Props> = ({ threadId, message: m, canManage, telegramCanDelete, onUpdated, onDownload }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const isOutbound = m.direction === 'outbound';
  // Telegram'ga bog'liq xabar (platformadan yuborilgan javob) - tahrir/o'chirish
  // mijozning Telegram chatida ham aks etadi. Ichki eslatma Telegram'da yo'q.
  const inTelegram = !m.is_internal && m.telegram_message_id != null;
  // Fayl-xabar: "matn" = fayl izohi (bo'sh bo'lishi mumkin), Telegram'da
  // editMessageCaption; o'chirishda fayl bildirishnomaga almashtiriladi.
  const hasAttachment = m.attachments.length > 0;

  const startEdit = () => {
    setDraft(m.text || '');
    setError('');
    setEditing(true);
    setMenuOpen(false);
  };

  const saveEdit = async () => {
    const text = draft.trim();
    if (!text && !hasAttachment) { setError("Matn bo'sh bo'lishi mumkin emas"); return; }
    if (text === (m.text || '')) { setEditing(false); return; }
    setBusy(true);
    setError('');
    try {
      const res = await api.patch(`/inquiries/${threadId}/messages/${m.id}`, { text });
      onUpdated(res.data);
      setEditing(false);
    } catch (e: any) {
      setError(errDetail(e, "Xabarni tahrirlab bo'lmadi"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    const question = m.is_internal
      ? "Ichki eslatma o'chirilsinmi?\n\nBu mijozga ketmagan — faqat platformada \"o'chirildi\" deb belgilanadi."
      : inTelegram
        ? telegramCanDelete
          ? `${hasAttachment ? 'Fayl' : 'Xabar'} o'chirilsinmi?\n\nBu xabar mijoz chatidan butunlay o'chiriladi va platformada o'chirilgan deb belgilanadi.`
          : hasAttachment
            ? "Fayl o'chirilsinmi?\n\nFayl mijoz chatida 'xato yuborildi' bildirishnomasiga almashtiriladi (izohi '🗑 (xabar bekor qilindi)'). Platformada original fayl o'chirilgan deb belgilangan holda saqlanadi."
            : "Xabar o'chirilsinmi?\n\nBu xabar mijoz chatida '🗑 (xabar bekor qilindi)' ga almashtiriladi va platformada o'chirilgan deb belgilanadi."
        : "Xabar o'chirilsinmi?\n\nBu xabar Telegram'ga bog'lanmagan - faqat platformada o'chirilgan deb belgilanadi.";
    if (!window.confirm(question)) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.delete(`/inquiries/${threadId}/messages/${m.id}`);
      onUpdated(res.data);
    } catch (e: any) {
      setError(errDetail(e, "Xabarni o'chirib bo'lmadi"));
    } finally {
      setBusy(false);
    }
  };

  const bubbleCls = m.is_deleted
    ? 'bg-gray-100 text-gray-400 border border-gray-200'
    : m.is_internal
      ? 'bg-yellow-50 border border-yellow-300 border-dashed text-gray-800'
      : isOutbound ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border';
  const metaCls = m.is_deleted
    ? 'text-gray-400'
    : m.is_internal ? 'text-yellow-700' : isOutbound ? 'text-blue-100' : 'text-gray-400';
  const wrapCls = m.is_internal ? 'justify-center' : isOutbound ? 'justify-end' : 'justify-start';
  const widthCls = m.is_internal ? 'w-full max-w-[85%] rounded-lg' : 'max-w-[75%] rounded-xl';

  const showMenu = canManage && !m.is_deleted && !editing;

  return (
    <div className={`flex ${wrapCls} group`}>
      <div className={`relative ${widthCls} px-3 py-2 ${bubbleCls}`}>
        {showMenu && (
          <div className="absolute top-1 right-1">
            <button onClick={() => setMenuOpen(v => !v)} disabled={busy} title="Amallar"
              className={`text-xs leading-none px-1.5 py-0.5 rounded opacity-60 hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 ${menuOpen ? 'md:opacity-100' : ''} ${isOutbound && !m.is_internal ? 'hover:bg-blue-500' : 'hover:bg-gray-200'}`}>
              ⋯
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-44 bg-white border rounded-lg shadow-lg z-10 text-sm text-gray-700 overflow-hidden">
                <button onClick={startEdit} className="block w-full text-left px-3 py-1.5 hover:bg-gray-50">✏️ {hasAttachment ? 'Izohni tahrirlash' : 'Tahrirlash'}</button>
                <button onClick={handleDelete} className="block w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600">🗑 O'chirish</button>
              </div>
            )}
          </div>
        )}

        {m.is_internal && (
          <p className={`text-[10px] font-semibold mb-0.5 pr-5 ${m.is_deleted ? 'text-gray-400' : 'text-yellow-800'}`}>
            🔒 Ichki eslatma{m.sender?.full_name ? ` · ${m.sender.full_name}` : ''}
          </p>
        )}
        {!m.is_internal && isOutbound && m.sender?.full_name && (
          <p className="text-[10px] opacity-75 mb-0.5 pr-5">{m.sender.full_name}</p>
        )}

        {editing ? (
          <div className="space-y-1.5">
            <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} autoFocus
              placeholder={hasAttachment ? "Fayl izohi (bo'sh qoldirilsa izoh olib tashlanadi)" : undefined}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                if (e.key === 'Escape') setEditing(false);
              }}
              className="w-full min-w-[14rem] border rounded-lg px-2 py-1.5 text-sm text-gray-800 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className={`text-[10px] ${m.is_internal ? 'text-yellow-800' : isOutbound ? 'text-blue-100' : 'text-gray-500'}`}>
              {m.is_internal
                ? "⚠️ Bu mijozga ketmagan — Telegram chaqirilmaydi, faqat eslatma o'zgaradi."
                : inTelegram
                  ? hasAttachment
                    ? "Mijozning Telegram chatida fayl izohi tahrirlanadi (faylning o'zi o'zgarmaydi)."
                    : "Mijozning Telegram chatida ham tahrirlanadi."
                  : ''}
            </p>
            <div className="flex gap-1.5 justify-end">
              <button onClick={() => setEditing(false)} disabled={busy}
                className="px-2 py-1 text-xs rounded border bg-white text-gray-600 hover:bg-gray-50">Bekor</button>
              <button onClick={saveEdit} disabled={busy || (!draft.trim() && !hasAttachment)}
                className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {busy ? '...' : 'Saqlash'}
              </button>
            </div>
          </div>
        ) : (
          m.text && (
            <p className={`text-sm whitespace-pre-wrap break-words ${m.is_deleted ? 'line-through' : ''} ${showMenu ? 'pr-5' : ''}`}>{m.text}</p>
          )
        )}

        {m.attachments.map(att => (
          <button key={att.id} onClick={() => onDownload(att)}
            className={`flex items-center gap-1.5 mt-1.5 text-xs px-2 py-1 rounded-lg ${isOutbound && !m.is_internal && !m.is_deleted ? 'bg-blue-500 hover:bg-blue-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
            📎 <span className="truncate max-w-[10rem]">{att.original_filename}</span>
            {att.size_bytes ? <span className="opacity-70">({formatSize(att.size_bytes)})</span> : null}
          </button>
        ))}

        <p className={`text-[10px] mt-1 ${metaCls}`}>
          {timeShort(m.created_at)}
          {m.is_internal && !m.is_deleted && " · mijozga ko'rinmaydi"}
          {m.edited_at && (
            <button onClick={() => setShowHistory(v => !v)} className="ml-1 underline decoration-dotted hover:opacity-80"
              title="Tahrir tarixini ko'rish">
              (tahrirlangan)
            </button>
          )}
        </p>

        {m.is_deleted && (
          <p className="text-[11px] mt-0.5 text-gray-500 not-italic">
            🗑 O'chirildi — {m.deleted_by?.full_name || 'Telegram tomonida'}{m.deleted_at ? `, ${timeShort(m.deleted_at)}` : ''}
          </p>
        )}

        {showHistory && m.edits.length > 0 && (
          <div className="mt-1.5 rounded-lg bg-white/90 text-gray-700 border px-2 py-1.5 space-y-1">
            <p className="text-[10px] font-semibold text-gray-500">Tahrir tarixi (eski matnlar):</p>
            {m.edits.map(ed => (
              <div key={ed.id} className="text-xs border-t first:border-t-0 pt-1 first:pt-0">
                <p className="whitespace-pre-wrap break-words line-through text-gray-500">{ed.old_text || '—'}</p>
                <p className="text-[10px] text-gray-400">
                  {timeShort(ed.edited_at)} · {ed.edited_by?.full_name || 'Telegram tomonida'}
                </p>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-xs mt-1 px-1.5 py-1 rounded bg-red-50 text-red-600 border border-red-200">{error}</p>}
      </div>
    </div>
  );
};

export default InquiryMessageBubble;
